import { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Check } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UNREAD_POLL_INTERVAL_MS = 30000;

export const NotificationBell = ({ isAuthenticated }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      setUnreadCount(res.data.count);
    } catch (error) {
      console.error('Failed to fetch unread notifications count:', error);
    }
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(res.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, UNREAD_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchUnreadCount]);

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggle = () => {
    if (!isOpen) fetchNotifications();
    setIsOpen((v) => !v);
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="relative" ref={containerRef}>
      <button onClick={toggle} className="relative" data-testid="notification-bell">
        <Bell className="w-6 h-6 text-[#1C241E] hover:text-[#D96C4A] transition-colors" />
        {unreadCount > 0 && (
          <span className="absolute -top-2 -right-2 bg-[#D96C4A] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" data-testid="notification-count">
            {unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-[rgba(28,36,30,0.12)] rounded-xl shadow-lg z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[rgba(28,36,30,0.12)]">
            <h3 className="font-medium text-sm">Notifications</h3>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-[#D96C4A] hover:text-[#C05A3D] flex items-center gap-1" data-testid="mark-all-read">
                <Check className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="text-sm text-[#5C6B61] text-center py-8">No notifications</p>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  onClick={() => !n.read && markAsRead(n.id)}
                  className={`px-4 py-3 border-b border-[rgba(28,36,30,0.06)] cursor-pointer hover:bg-[#FAFAF8] ${!n.read ? 'bg-[#FFF9F0]' : ''}`}
                  data-testid={`notification-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <div className="w-2 h-2 rounded-full bg-[#D96C4A] mt-1.5 shrink-0" />}
                    <div className={!n.read ? '' : 'ml-4'}>
                      <p className="text-sm font-medium text-[#1C241E]">{n.title}</p>
                      <p className="text-xs text-[#5C6B61] mt-0.5">{n.message}</p>
                      <p className="text-xs text-[#5C6B61] mt-1">{new Date(n.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
